import { ICachedAddress, IPendingRedsysPayment } from '../models'
import { Language } from './literals-service'

type StorageType = 'local' | 'session'

// Every brand used to store under '@AMB'; each one now has its own prefix, so builds served from the
// same origin (e.g. localhost) don't share the language or the cached addresses.
const LEGACY_PREFIX = '@AMB'

class StorageService {
  private prefix = `@${process.env.REACT_APP_storagePrefix || 'AMB'}`
  private keys = {
    language: `${this.prefix}:language`,
    routeName: `${this.prefix}:route`,
    isInstructionsExpanded: `${this.prefix}:isInstructionsExpanded`,
    cachedAddresses: `${this.prefix}:cachedAddresses`,
    pendingRedsysPayment: `${this.prefix}:pendingRedsysPayment`
  }

  constructor() {
    this.migrateLegacyKeys()
  }

  // Moves what was saved under the old shared prefix to this brand's one, once. Each brand has its
  // own domain in production, so whatever is there under '@AMB' belongs to it.
  private migrateLegacyKeys(): void {
    if (this.prefix === LEGACY_PREFIX) return
    try {
      Object.values(this.keys).forEach(key => {
        const legacyKey = key.replace(this.prefix, LEGACY_PREFIX)
        const legacyValue = localStorage.getItem(legacyKey)
        if (legacyValue === null) return
        if (localStorage.getItem(key) === null) localStorage.setItem(key, legacyValue)
        localStorage.removeItem(legacyKey)
      })
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error)
    }
  }

  public setLanguage(language: Language): void {
    return this.setByKey(this.keys.language, language)
  }

  public getLanguage(): Language {
    return this.getByKey(this.keys.language)
  }

  public removeLanguage(): void {
    return this.removeByKey(this.keys.language)
  }

  public setRouteName(routeName: string): void {
    return this.setByKey(this.keys.routeName, routeName)
  }

  public getRouteName(): string {
    return this.getByKey(this.keys.routeName)
  }

  public removeRouteName(): void {
    return this.removeByKey(this.keys.routeName)
  }

  public async setIsInstructionsExpanded(isInstructionsExpanded: boolean): Promise<void> {
    return this.setByKey(this.keys.isInstructionsExpanded, isInstructionsExpanded)
  }

  public async removeInstructionsExpanded(): Promise<void> {
    return this.removeByKey(this.keys.isInstructionsExpanded);
  }

  public async getIsInstructionsExpanded(): Promise<boolean> {
    return this.getByKey(this.keys.isInstructionsExpanded)
  }

  public getCachedAddresses(): ICachedAddress[] {
    const stored = this.getByKey(this.keys.cachedAddresses)
    return Array.isArray(stored) ? stored : []
  }

  public setCachedAddresses(addresses: ICachedAddress[]): void {
    return this.setByKey(this.keys.cachedAddresses, addresses)
  }

  // The bank sends the browser back without the reservation, and only to this same tab.
  public setPendingRedsysPayment(payment: IPendingRedsysPayment): void {
    return this.setByKey(this.keys.pendingRedsysPayment, payment, 'session')
  }

  public getPendingRedsysPayment(): IPendingRedsysPayment | null {
    return this.getByKey(this.keys.pendingRedsysPayment, 'session') ?? null
  }

  public removePendingRedsysPayment(): void {
    return this.removeByKey(this.keys.pendingRedsysPayment, 'session')
  }

  private storage(storageType: StorageType): Storage {
    return storageType === 'session' ? sessionStorage : localStorage
  }

  private getByKey(key: string, storageType: StorageType = 'local'): any {
    try {
      const value = this.storage(storageType).getItem(key)
      return value ? JSON.parse(value) : null
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error)
    }
  }

  private setByKey(key: string, value: any, storageType: StorageType = 'local'): void {
    try {
      this.storage(storageType).setItem(key, JSON.stringify(value))
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error)
    }
  }

  private removeByKey(key: string, storageType: StorageType = 'local'): any {
    try {
      this.storage(storageType).removeItem(key)
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error)
    }
  }
}

export default new StorageService()
